import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';
import { _getToken } from 'src/Config/axiosApi';
import { NotificationEvent } from '../types/notifications.types';

/**
 * Connects to the notifications-websocket service and exposes a
 * stream of new `NotificationEvent`s. The page consumes these and
 * prepends them to its react-query infinite list.
 *
 * Auth: the same JWT pattern as `useUnwindingSocket` —
 * `socket.io-client` reads the current token from a callback so
 * silent refresh / logout propagate without a manual reconnect.
 *
 * Connection model: separate Socket.io connection from `/unwind`
 * (different host) — by design. Notification fanout cannot
 * head-of-line block the market-data WS this way.
 */
export function useNotificationsSocket() {
  const wsUrl = import.meta.env.VITE_NOTIFICATIONS_WS_URL;
  const accessToken = _getToken();

  const { socket } = useSocket(wsUrl || '', {
    enabled: !!wsUrl && !!accessToken,
    transports: ['websocket'],
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() }),
  });

  const [latestEvent, setLatestEvent] = useState<NotificationEvent | null>(
    null,
  );
  const [bufferedCount, setBufferedCount] = useState(0);
  /**
   * Bumps every time the socket transitions disconnected → connected.
   * The page reads this to refetch its REST history on reconnect:
   * Redis pub/sub is fire-and-forget, so any event published while
   * the gateway pod was restarting (or the browser was sleeping) is
   * lost on the realtime channel. Mongo persistence is the durable
   * record — refetching the latest page closes the gap.
   *
   * Increments on EVERY reconnect, not just the first, so a long
   * tab-sleep + wake cycle re-pages too.
   */
  const [reconnectCount, setReconnectCount] = useState(0);
  const hasSubscribed = useRef(false);
  // Tracks whether we've ever observed a disconnect on this socket
  // instance — so the very first `connect` after mount doesn't bump
  // the reconnect counter (initial mount is not a reconnect).
  const hasDisconnected = useRef(false);

  // Subscribe on every (re)connect.
  useEffect(() => {
    const subscribe = () => {
      if (!hasSubscribed.current) {
        socket.emit('subscribe_notifications');
        hasSubscribed.current = true;
      }
      if (hasDisconnected.current) {
        // Genuine reconnect — bump the counter so the page refetches.
        setReconnectCount((n) => n + 1);
        hasDisconnected.current = false;
      }
    };
    const reset = () => {
      hasSubscribed.current = false;
      hasDisconnected.current = true;
    };

    socket.on('connect', subscribe);
    socket.on('disconnect', reset);
    if (socket.connected) subscribe();
    else reset();

    return () => {
      socket.off('connect', subscribe);
      socket.off('disconnect', reset);
    };
  }, [socket, socket.connected]);

  const { lastMessage } = useSocketEvent<NotificationEvent>(
    socket,
    'notification',
  );

  useEffect(() => {
    if (!lastMessage) return;
    setLatestEvent(lastMessage);
    setBufferedCount((n) => n + 1);
  }, [lastMessage]);

  /** Page calls this after consuming `latestEvent` so the same event
   *  isn't re-applied on re-renders. */
  const acknowledge = useCallback(() => {
    setLatestEvent(null);
  }, []);

  /** Reset the buffered count badge (e.g. after the page refocuses). */
  const resetBufferedCount = useCallback(() => {
    setBufferedCount(0);
  }, []);

  return {
    isConnected: socket.connected,
    latestEvent,
    bufferedCount,
    /**
     * Counter that increments each time the socket transitions
     * disconnected → connected. The page subscribes to it as a
     * react-query refetch trigger so any event missed during the
     * disconnect (Redis pub/sub is fire-and-forget) gets picked up
     * from Mongo via REST.
     */
    reconnectCount,
    acknowledge,
    resetBufferedCount,
  };
}
